import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../cn.js';

export interface ModalProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** `md` (default) is the original single-column dialog width; `lg` is for multi-step forms like
   * Add Employee, wide enough for a two-column field grid without wrapping every label; `xl`
   * (max-w-3xl) and `2xl` (max-w-4xl) are for wide location maps and branch forms. */
  size?: 'md' | 'lg' | 'xl' | '2xl';
  /** Below the `sm` breakpoint (640px) the dialog fills the whole screen as a sheet instead of
   * floating as a card. Opt-in: other dialogs are unchanged. */
  sheetOnPhone?: boolean;
  /**
   * The form inside has unsaved changes. Escape, a backdrop click or the close button then ask
   * "Discard changes?" inside the dialog instead of closing straight away. Opt-in.
   */
  dirty?: boolean;
  /** Show an X in the header. Opt-in so existing dialogs (and their tests) are unchanged. */
  showCloseButton?: boolean;
  /**
   * Move focus into the dialog when it opens, keep Tab inside it, and give focus back to whatever
   * opened it when it closes. Opt-in for the same reason.
   */
  trapFocus?: boolean;
}

const MAX_WIDTH: Record<NonNullable<ModalProps['size']>, string> = {
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-3xl',
  '2xl': 'max-w-4xl',
};

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * The generic dialog primitive, promoted from `apps/web/src/features/employees/Modal.tsx` once a
 * second feature (Training's task player and its confirmation dialogs) needed one — exactly the
 * "generalise on second use" trigger that component's own doc comment asked for. By default it is
 * uncontrolled beyond `open`, closes on Escape or a backdrop click, and does not trap focus;
 * `dirty`, `showCloseButton` and `trapFocus` opt a dialog into the fuller behaviour.
 *
 * PORTALED to `document.body` — not rendered in place. A dialog opened from a page that also
 * renders `Table.tsx` (Requisitions, Offers, etc.) used to lose the stacking fight to that table's
 * `position: sticky` header (`z-20`) and pinned-right column (`z-30`): same tier/z-index as this
 * modal's old in-place `z-20`, so the table painted on top of the backdrop and dialog instead of
 * behind it. Portaling to `document.body` removes it from that stacking context entirely — same
 * fix and same reasoning as this package's `DropdownMenu`.
 */
export function Modal({
  open,
  title,
  onClose,
  children,
  size = 'md',
  sheetOnPhone = false,
  dirty = false,
  showCloseButton = false,
  trapFocus = false,
}: ModalProps) {
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const panel = useRef<HTMLDivElement>(null);

  // Every way of leaving goes through here, so an unsaved form is never dropped by accident.
  const requestClose = useRef(() => {});
  requestClose.current = () => {
    if (dirty && !confirmingDiscard) {
      setConfirmingDiscard(true);
      return;
    }
    setConfirmingDiscard(false);
    onClose();
  };

  useEffect(() => {
    if (!open) setConfirmingDiscard(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        if (confirmingDiscard) setConfirmingDiscard(false);
        else requestClose.current();
        return;
      }
      if (trapFocus && event.key === 'Tab' && panel.current) {
        const items = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE));
        if (items.length === 0) return;
        const first = items[0]!;
        const last = items[items.length - 1]!;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, confirmingDiscard, trapFocus]);

  useEffect(() => {
    if (!open || !trapFocus) return;
    const opener = document.activeElement as HTMLElement | null;
    const first = panel.current?.querySelector<HTMLElement>(
      'input:not([disabled]), select:not([disabled]), textarea:not([disabled])',
    );
    (first ?? panel.current)?.focus();
    return () => opener?.focus?.();
  }, [open, trapFocus]);

  if (!open) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/40 ${
        sheetOnPhone ? 'pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] sm:p-4' : 'p-4'
      }`}
      onClick={() => requestClose.current()}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cn(
          'w-full overflow-y-auto overscroll-contain border border-border bg-surface shadow-lg focus:outline-none',
          MAX_WIDTH[size],
          sheetOnPhone
            ? 'h-full max-h-none rounded-none p-4 sm:h-auto sm:max-h-[90dvh] sm:rounded-xl sm:p-6'
            : 'max-h-[90dvh] rounded-xl p-6',
        )}
        onClick={(event) => event.stopPropagation()}
      >
        {showCloseButton ? (
          <div className="mb-4 flex items-start justify-between gap-3">
            <h2 className="font-display text-lg font-bold text-fg">{title}</h2>
            <button
              type="button"
              aria-label="Close"
              onClick={() => requestClose.current()}
              className="-mr-2 -mt-1 inline-flex size-11 shrink-0 items-center justify-center rounded-md text-fg-muted hover:bg-bg-subtle hover:text-fg sm:size-8"
            >
              <X className="size-4" aria-hidden="true" />
            </button>
          </div>
        ) : (
          <h2 className="mb-4 font-display text-lg font-bold text-fg">{title}</h2>
        )}
        {confirmingDiscard && (
          <div
            role="alert"
            className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-md bg-warning-subtle px-3 py-2 text-sm text-fg"
          >
            <span>Discard your unsaved changes?</span>
            <span className="flex gap-2">
              <button
                type="button"
                className="min-h-11 rounded-md px-3 text-sm font-semibold text-fg-muted hover:bg-bg-subtle sm:min-h-8"
                onClick={() => setConfirmingDiscard(false)}
              >
                Keep editing
              </button>
              <button
                type="button"
                className="min-h-11 rounded-md bg-danger px-3 text-sm font-semibold text-fg-on-accent hover:opacity-90 sm:min-h-8"
                onClick={() => {
                  setConfirmingDiscard(false);
                  onClose();
                }}
              >
                Discard
              </button>
            </span>
          </div>
        )}
        {children}
      </div>
    </div>,
    document.body,
  );
}
